import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchReviewContext,
  fetchReviewCustomers,
  createReviewServiceRecord,
} from '../services/reviewAppService';
import './ReviewAppPage.css';

/** UI label → API value (backend enums stay Turkish) */
const JOB_TYPES = [
  { value: 'Kurulum', label: 'Installation' },
  { value: 'Bakım', label: 'Maintenance' },
  { value: 'Arıza', label: 'Repair' },
  { value: 'İstek ve Şikayet', label: 'Request & Complaint' },
];

const PRIORITIES = [
  { value: 'Acil', label: 'Urgent' },
  { value: 'Yüksek', label: 'High' },
  { value: 'Orta', label: 'Medium' },
  { value: 'Düşük', label: 'Low' },
];

const ReviewAppPage = ({ onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [teknikServis, setTeknikServis] = useState(null);
  const [musteriler, setMusteriler] = useState([]);
  const [musteriId, setMusteriId] = useState('');
  const [adresId, setAdresId] = useState('');
  const [islemTipi, setIslemTipi] = useState('Arıza');
  const [onemDerecesi, setOnemDerecesi] = useState('Orta');
  const [notlar, setNotlar] = useState('');

  const selectedMusteri = useMemo(
    () => musteriler.find((m) => String(m.id) === String(musteriId)) || null,
    [musteriler, musteriId]
  );

  const adresler = selectedMusteri?.adresler || [];
  const isRequestComplaint = islemTipi === 'İstek ve Şikayet';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ctxRes, musteriRes] = await Promise.all([
        fetchReviewContext(),
        fetchReviewCustomers(),
      ]);
      if (ctxRes?.basarili === false) {
        throw new Error(ctxRes?.mesaj || 'Failed to load context');
      }
      if (musteriRes?.basarili === false) {
        throw new Error(musteriRes?.mesaj || 'Failed to load customers');
      }
      setTeknikServis(ctxRes?.veri?.teknikServis || null);
      const list = musteriRes?.veri?.musteriler || [];
      setMusteriler(list);
      if (list.length === 1) {
        setMusteriId(String(list[0].id));
        const firstAdres = list[0].adresler?.[0];
        if (firstAdres) setAdresId(String(firstAdres.id));
      }
    } catch (err) {
      setError(err?.message || err?.mesaj || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedMusteri) {
      setAdresId('');
      return;
    }
    const preferred =
      selectedMusteri.adresler?.find((a) => a.varsayilan) ||
      selectedMusteri.adresler?.[0];
    setAdresId(preferred ? String(preferred.id) : '');
  }, [selectedMusteri]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!musteriId) {
      setError('Please select a customer');
      return;
    }
    if (!isRequestComplaint && !adresId) {
      setError('Please select an address');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        musteriId,
        islemTipi,
        cihazSecilmedi: true,
        cihazAciklama: 'Review test record',
      };
      if (adresId) payload.adresId = adresId;
      if (!isRequestComplaint) {
        payload.onemDerecesi = onemDerecesi;
      }
      if (islemTipi === 'Arıza') {
        payload.arizaNotu = notlar.trim() || 'Review test repair';
      }
      if (isRequestComplaint) {
        payload.istekSikayetKategorisi = 'Diğer';
        payload.istekSikayetTuru = 'İstek';
        payload.istekSikayetNotu = notlar.trim() || 'Review test';
      }
      if (notlar.trim() && !isRequestComplaint && islemTipi !== 'Arıza') {
        payload.notlar = notlar.trim();
      }

      const res = await createReviewServiceRecord(payload);
      if (res?.basarili === false) {
        throw new Error(res?.mesaj || 'Failed to create service record');
      }
      const sk = res?.veri?.servisKaydi || res?.veri || {};
      const kod = sk.kod || sk.servisKaydiNo || sk.id || '';
      setSuccess(
        kod ? `Service record created: ${kod}` : 'Service record created'
      );
      setNotlar('');
    } catch (err) {
      setError(err?.message || err?.mesaj || 'Failed to create service record');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="review-app-root">
      <header className="review-app-header">
        <div className="review-app-brand">
          <img src="/assets/img/logo.png" alt="" className="review-app-logo" />
          <div>
            <h1>Review App</h1>
            <p>Create a service record for a test customer</p>
          </div>
        </div>
        <button type="button" className="review-app-logout" onClick={onLogout}>
          Sign out
        </button>
      </header>

      <main className="review-app-main">
        {loading ? (
          <p className="review-app-muted">Loading...</p>
        ) : (
          <form className="review-app-card" onSubmit={handleSubmit}>
            {teknikServis && (
              <div className="review-app-meta">
                Technical service:{' '}
                <strong>{teknikServis.ad || teknikServis.id}</strong>
                {teknikServis.kod != null ? ` (${teknikServis.kod})` : ''}
              </div>
            )}

            {error && <div className="review-app-error">{error}</div>}
            {success && <div className="review-app-success">{success}</div>}

            <label className="review-app-field">
              <span>Test customer</span>
              <select
                value={musteriId}
                onChange={(e) => setMusteriId(e.target.value)}
                required
              >
                <option value="">Select</option>
                {musteriler.map((m) => (
                  <option key={m.id} value={m.id}>
                    {[m.adi, m.soyadi].filter(Boolean).join(' ')}
                    {m.telefon ? ` — ${m.telefon}` : ''}
                  </option>
                ))}
              </select>
            </label>

            <label className="review-app-field">
              <span>Address</span>
              <select
                value={adresId}
                onChange={(e) => setAdresId(e.target.value)}
                disabled={!musteriId || adresler.length === 0}
                required={!isRequestComplaint}
              >
                <option value="">Select</option>
                {adresler.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.adres || a.id}
                    {a.varsayilan ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </label>

            <label className="review-app-field">
              <span>Job type</span>
              <select value={islemTipi} onChange={(e) => setIslemTipi(e.target.value)}>
                {JOB_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>

            {!isRequestComplaint && (
              <label className="review-app-field">
                <span>Priority</span>
                <select
                  value={onemDerecesi}
                  onChange={(e) => setOnemDerecesi(e.target.value)}
                >
                  {PRIORITIES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="review-app-field">
              <span>Note</span>
              <textarea
                value={notlar}
                onChange={(e) => setNotlar(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Optional"
              />
            </label>

            <button type="submit" className="review-app-submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create service record'}
            </button>
          </form>
        )}
      </main>
    </div>
  );
};

export default ReviewAppPage;
